import React, { useState, useCallback } from 'react';
import { Sparkles, X, Search, RefreshCw, ArrowRight, Lock, ChevronDown, ChevronUp, Zap, Target, Layers, ShoppingCart, DollarSign, MessageSquare } from 'lucide-react';
import { Tool, PlanName } from '../types';
import { supabase, SUPABASE_ANON_KEY, SUPABASE_URL } from '../services/supabaseClient';

interface AdvisorPanelProps {
  isOpen: boolean;
  onClose: () => void;
  tools: Tool[];
  plan: PlanName;
  companyId: string | null;
}

interface Recommendation {
  toolName: string;
  reason: string;
  estimatedCost: string;
  compatibilityScore: number;
  migrationEffort: 'low' | 'medium' | 'high';
  pros: string[];
  cons: string[];
}

interface AdvisorResponse {
  type: string;
  confidence: number;
  summary: string;
  recommendations: Recommendation[];
  provider?: 'mistral' | 'fallback';
  savingsEstimate?: string;
  negotiationBrief?: {
    leveragePoints: string[];
    recommendedActions: string[];
    talkingPoints: string[];
  };
  dataPoints?: string[];
}

interface UsageInfo {
  queriesUsed: number;
  queriesIncluded: number;
  isOverage: boolean;
  overageAmount: number;
  spendingCap: number;
}

type UseCase = 'gap_analysis' | 'replacement' | 'consolidation' | 'new_tool' | 'negotiation' | 'freeform';

const USE_CASES: { id: UseCase; label: string; desc: string; icon: React.ReactNode; minTier: PlanName }[] = [
  { id: 'gap_analysis', label: 'Analyze my stack', desc: 'Find missing tools', icon: <Search className="w-4 h-4" />, minTier: 'trial' },
  { id: 'replacement', label: 'Find replacement', desc: 'Replace underperforming tool', icon: <RefreshCw className="w-4 h-4" />, minTier: 'trial' },
  { id: 'consolidation', label: 'Consolidate tools', desc: 'Reduce overlap & costs', icon: <Layers className="w-4 h-4" />, minTier: 'trial' },
  { id: 'new_tool', label: 'Find new tool', desc: 'Budget-aware selection', icon: <ShoppingCart className="w-4 h-4" />, minTier: 'pro' },
  { id: 'negotiation', label: 'Negotiate renewal', desc: 'Get a better deal', icon: <DollarSign className="w-4 h-4" />, minTier: 'pro' },
  { id: 'freeform', label: 'Ask anything', desc: 'Strategic IT questions', icon: <MessageSquare className="w-4 h-4" />, minTier: 'business' },
];

const TIER_ORDER: PlanName[] = ['trial', 'starter', 'pro', 'business', 'tester'];

function tierAccessible(userPlan: PlanName, required: PlanName): boolean {
  return TIER_ORDER.indexOf(userPlan) >= TIER_ORDER.indexOf(required);
}

const TIER_LABELS: Record<string, string> = { pro: 'PRO', business: 'BIZ' };

export const AdvisorPanel: React.FC<AdvisorPanelProps> = ({ isOpen, onClose, tools, plan, companyId }) => {
  const [activeUC, setActiveUC] = useState<UseCase | null>(null);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<AdvisorResponse | null>(null);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDataPoints, setShowDataPoints] = useState(false);
  const [rawPayload, setRawPayload] = useState<string | null>(null);
  const [lastResponseAt, setLastResponseAt] = useState<string | null>(null);

  const [selectedToolId, setSelectedToolId] = useState('');
  const [newToolNeed, setNewToolNeed] = useState('');
  const [newToolBudget, setNewToolBudget] = useState('');
  const [freeformQuestion, setFreeformQuestion] = useState('');

  const runQuery = useCallback(async (useCase: UseCase) => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    setResponse(null);
    setRawPayload(null);

    let input: Record<string, unknown> = {};
    if (useCase === 'replacement' || useCase === 'negotiation') input = { toolId: selectedToolId };
    if (useCase === 'new_tool') input = { need: newToolNeed, budget: newToolBudget ? Number(newToolBudget) : undefined };
    if (useCase === 'freeform') input = { question: freeformQuestion };

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        setError('Your session expired. Please sign in again.');
        return;
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/ai-advisor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ useCase, input }),
      });

      const data = await res.json();
      setRawPayload(JSON.stringify(data, null, 2));
      if (!res.ok) {
        setError(data?.message || data?.error || 'An error occurred');
        return;
      }

      const rawRecommendation = data?.recommendation;
      let parsedRecommendation: any = rawRecommendation;
      if (typeof rawRecommendation === 'string') {
        try {
          parsedRecommendation = JSON.parse(rawRecommendation);
        } catch {
          parsedRecommendation = {
            type: useCase,
            confidence: 60,
            summary: rawRecommendation,
            recommendations: [],
            dataPoints: [],
          };
        }
      }

      if (!parsedRecommendation || typeof parsedRecommendation !== 'object') {
        setError('Advisor returned an empty response. Please try again.');
        return;
      }

      const normalizeConfidence = (value: unknown): number => {
        const n = Number(value);
        if (Number.isNaN(n)) return 60;
        // Some models return confidence in 0..1 instead of 0..100.
        const pct = n <= 1 ? n * 100 : n;
        return Math.max(0, Math.min(100, Math.round(pct)));
      };

      const normalizedRecommendations: Recommendation[] = Array.isArray(parsedRecommendation.recommendations)
        ? parsedRecommendation.recommendations.map((rec: unknown): Recommendation => {
            if (typeof rec === 'string') {
              return {
                toolName: 'Recommendation',
                reason: rec,
                estimatedCost: 'N/A',
                compatibilityScore: 70,
                migrationEffort: 'low',
                pros: [],
                cons: [],
              };
            }

            const obj = (rec && typeof rec === 'object') ? rec as Record<string, unknown> : {};
            return {
              toolName: String(obj.toolName ?? obj.tool ?? 'Recommendation'),
              reason: String(obj.reason ?? 'No details provided.'),
              estimatedCost: String(obj.estimatedCost ?? obj.cost ?? 'N/A'),
              compatibilityScore: Math.max(0, Math.min(100, Number(obj.compatibilityScore ?? 70))),
              migrationEffort: (obj.migrationEffort === 'low' || obj.migrationEffort === 'medium' || obj.migrationEffort === 'high')
                ? obj.migrationEffort
                : 'medium',
              pros: Array.isArray(obj.pros) ? obj.pros.map(String) : [],
              cons: Array.isArray(obj.cons) ? obj.cons.map(String) : [],
            };
          })
        : [];

      const normalizedDataPoints: string[] = Array.isArray(parsedRecommendation.dataPoints)
        ? parsedRecommendation.dataPoints.map((dp: unknown) => {
            if (typeof dp === 'string') return dp;
            if (dp && typeof dp === 'object') {
              const o = dp as Record<string, unknown>;
              const parts = [
                o.tool ? `Tool: ${String(o.tool)}` : '',
                o.role ? `Role: ${String(o.role)}` : '',
                o.criticality ? `Criticality: ${String(o.criticality)}` : '',
                o.cost != null ? `Cost: ${String(o.cost)}` : '',
                o.satisfaction != null ? `Satisfaction: ${String(o.satisfaction)}` : '',
              ].filter(Boolean);
              return parts.length > 0 ? parts.join(' | ') : JSON.stringify(o);
            }
            return String(dp);
          })
        : [];

      setResponse({
        type: typeof parsedRecommendation.type === 'string' ? parsedRecommendation.type : useCase,
        confidence: normalizeConfidence(parsedRecommendation.confidence),
        summary: typeof parsedRecommendation.summary === 'string'
          ? parsedRecommendation.summary
          : 'Analysis completed.',
        provider:
          data?.provider === 'mistral' || data?.provider === 'fallback'
            ? data.provider
            : (
                Array.isArray(parsedRecommendation.dataPoints) &&
                parsedRecommendation.dataPoints.some((dp: unknown) =>
                  typeof dp === 'string' && dp.toLowerCase().includes('fallback')
                )
              )
                ? 'fallback'
                : 'mistral',
        recommendations: normalizedRecommendations,
        savingsEstimate: parsedRecommendation.savingsEstimate,
        negotiationBrief: parsedRecommendation.negotiationBrief
          ? {
              leveragePoints: Array.isArray(parsedRecommendation.negotiationBrief.leveragePoints)
                ? parsedRecommendation.negotiationBrief.leveragePoints
                : [],
              recommendedActions: Array.isArray(parsedRecommendation.negotiationBrief.recommendedActions)
                ? parsedRecommendation.negotiationBrief.recommendedActions
                : [],
              talkingPoints: Array.isArray(parsedRecommendation.negotiationBrief.talkingPoints)
                ? parsedRecommendation.negotiationBrief.talkingPoints
                : [],
            }
          : undefined,
        dataPoints: normalizedDataPoints,
      });
      setUsage(data?.usage ?? null);
      setLastResponseAt(new Date().toISOString());
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedToolId, newToolNeed, newToolBudget, freeformQuestion]);

  const reset = () => {
    setActiveUC(null);
    setResponse(null);
    setError(null);
    setShowDataPoints(false);
    setRawPayload(null);
    setLastResponseAt(null);
  };

  const handleUseCaseClick = useCallback((useCase: UseCase, accessible: boolean) => {
    if (!accessible) return;
    setActiveUC(useCase);

    // These scenarios do not require extra user input, so launch immediately.
    if (useCase === 'gap_analysis' || useCase === 'consolidation') {
      void runQuery(useCase);
    }
  }, [runQuery]);

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] bg-white shadow-2xl z-[200] flex flex-col border-l border-gray-200 animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <h2 className="font-bold text-lg">Oasix Advisor</h2>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/20 transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Usage bar */}
      {usage && (
        <div className="px-5 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-500 flex justify-between items-center">
          <span>{usage.queriesUsed}/{usage.queriesIncluded} queries used</span>
          {usage.isOverage && <span className="text-amber-600">Overage: €{usage.overageAmount.toFixed(2)} / €{usage.spendingCap} cap</span>}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {!activeUC && !response && (
          <>
            <p className="text-sm text-gray-500 mb-4">What can I help you with?</p>
            {USE_CASES.map(uc => {
              const accessible = tierAccessible(plan, uc.minTier);
              return (
                <button
                  type="button"
                  key={uc.id}
                  onClick={() => handleUseCaseClick(uc.id, accessible)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-all text-left ${
                    accessible
                      ? 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 cursor-pointer'
                      : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${accessible ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-200 text-gray-400'}`}>
                    {accessible ? uc.icon : <Lock className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-800">{uc.label}</span>
                      {!accessible && TIER_LABELS[uc.minTier] && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">{TIER_LABELS[uc.minTier]}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{uc.desc}</p>
                  </div>
                  {accessible && <ArrowRight className="w-4 h-4 text-gray-400" />}
                </button>
              );
            })}
          </>
        )}

        {/* Input forms */}
        {activeUC && !response && !loading && (
          <div className="space-y-4">
            <button type="button" onClick={reset} className="text-xs text-indigo-600 hover:underline">← Back</button>

            {(activeUC === 'replacement' || activeUC === 'negotiation') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select a tool</label>
                <select
                  value={selectedToolId}
                  onChange={e => setSelectedToolId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                >
                  <option value="">Choose a tool...</option>
                  {tools.map(t => (
                    <option key={t.id} value={t.id}>{t.name} {t.satisfactionScore != null && t.satisfactionScore < 50 ? '⚠️' : ''}</option>
                  ))}
                </select>
              </div>
            )}

            {activeUC === 'new_tool' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">What do you need?</label>
                  <input
                    value={newToolNeed}
                    onChange={e => setNewToolNeed(e.target.value)}
                    placeholder="e.g. A BI tool for marketing analytics"
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly budget (optional)</label>
                  <input
                    type="number"
                    value={newToolBudget}
                    onChange={e => setNewToolBudget(e.target.value)}
                    placeholder="e.g. 500"
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                  />
                </div>
              </>
            )}

            {activeUC === 'freeform' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your question</label>
                <textarea
                  value={freeformQuestion}
                  onChange={e => setFreeformQuestion(e.target.value)}
                  placeholder="Ask any question about your IT strategy..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm resize-none"
                />
              </div>
            )}

            <button
              type="button"
              onClick={() => runQuery(activeUC)}
              disabled={
                (activeUC === 'replacement' && !selectedToolId) ||
                (activeUC === 'negotiation' && !selectedToolId) ||
                (activeUC === 'new_tool' && !newToolNeed) ||
                (activeUC === 'freeform' && !freeformQuestion)
              }
              className="w-full py-2.5 bg-indigo-600 text-white rounded-lg font-medium text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Ask Advisor
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Analyzing your stack...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-700">{error}</p>
            <button type="button" onClick={reset} className="mt-2 text-xs text-red-600 hover:underline">Try again</button>
          </div>
        )}

        {/* Results */}
        {response && (
          <div className="space-y-4">
            {lastResponseAt && (
              <p className="text-[11px] text-emerald-600">
                Response received at {new Date(lastResponseAt).toLocaleTimeString()}
              </p>
            )}
            <button type="button" onClick={reset} className="text-xs text-indigo-600 hover:underline">← New query</button>

            {/* Summary */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-medium text-indigo-600 uppercase">{String(response.type).replace('_', ' ')}</span>
                <span className="ml-auto text-xs text-indigo-500">{response.confidence}% confidence</span>
              </div>
              <p className="text-[11px] text-indigo-500 mb-2">
                Source: {response.provider === 'fallback' ? 'Fallback (no Mistral output)' : 'Mistral'}
              </p>
              <p className="text-sm font-medium text-gray-800">{response.summary}</p>
              {response.savingsEstimate && (
                <p className="mt-2 text-sm font-semibold text-green-700 bg-green-50 rounded-lg px-3 py-1.5 inline-block">
                  💰 Potential savings: {response.savingsEstimate}
                </p>
              )}
            </div>

            {/* Recommendation cards */}
            {response.recommendations.map((rec, i) => {
              const recPros = Array.isArray(rec.pros) ? rec.pros : [];
              const recCons = Array.isArray(rec.cons) ? rec.cons : [];

              return (
              <div key={i} className="border border-gray-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-800 text-sm">{rec.toolName}</h4>
                  <span className="text-xs text-gray-500">{rec.estimatedCost}</span>
                </div>
                <p className="text-xs text-gray-600">{rec.reason}</p>

                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <Zap className="w-3 h-3 text-blue-500" />
                    <span className="text-gray-500">Compatibility: {rec.compatibilityScore}%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${rec.migrationEffort === 'low' ? 'bg-green-400' : rec.migrationEffort === 'medium' ? 'bg-yellow-400' : 'bg-red-400'}`} />
                    <span className="text-gray-500">Migration: {rec.migrationEffort}</span>
                  </div>
                </div>

                {(recPros.length > 0 || recCons.length > 0) && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {recPros.length > 0 && (
                      <div>
                        <p className="text-[10px] font-medium text-green-700 mb-0.5">Pros</p>
                        {recPros.map((p, j) => <p key={j} className="text-[11px] text-gray-600">+ {p}</p>)}
                      </div>
                    )}
                    {recCons.length > 0 && (
                      <div>
                        <p className="text-[10px] font-medium text-red-700 mb-0.5">Cons</p>
                        {recCons.map((c, j) => <p key={j} className="text-[11px] text-gray-600">− {c}</p>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
            })}

            {/* Negotiation brief */}
            {response.negotiationBrief && (
              <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-2">
                <h4 className="font-semibold text-amber-800 text-sm">Negotiation Brief</h4>
                {response.negotiationBrief.talkingPoints.map((tp, i) => (
                  <p key={i} className="text-xs text-amber-700">• {tp}</p>
                ))}
                {response.negotiationBrief.recommendedActions.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[10px] font-medium text-amber-800">Recommended actions:</p>
                    {response.negotiationBrief.recommendedActions.map((a, i) => (
                      <p key={i} className="text-xs text-amber-700">→ {a}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Data points */}
            {response.dataPoints && response.dataPoints.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowDataPoints(!showDataPoints)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                >
                  {showDataPoints ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  Reasoning ({response.dataPoints.length} points)
                </button>
                {showDataPoints && (
                  <div className="mt-1 space-y-1">
                    {response.dataPoints.map((dp, i) => (
                      <p key={i} className="text-[11px] text-gray-500">• {dp}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        {rawPayload && (
          <details className="mt-2">
            <summary className="text-xs text-gray-400 cursor-pointer">Debug: raw advisor payload</summary>
            <pre className="mt-2 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded p-2 overflow-auto max-h-48">
              {rawPayload}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};
